// Path: ./src/app/pages/CreateBannerPage/BannerContentTabs.tsx
import React, { useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import { useTranslation } from "react-i18next";
import { CreateGfBannerDto } from "@swagger/typescript-fetch-goflyer";

import { messages } from "../../../locales/messages";
import {
  BANNER_CONTENT_FIELDS,
  bannerFieldLimit,
  BANNER_LOCALE_SUFFIXES,
  BannerContentField,
  BannerLocaleSuffix,
  bannerContentField,
} from "../../../utils/banner/bannerFields";
import { Field, TextControl } from "../../components/FormKit/formControls";
import { useTranslateBannerContent } from "./useTranslateBannerContent";
import { Callout, token } from "../../components/FormKit/styled";

interface Props {
  banner?: CreateGfBannerDto;
  onFieldChange: (field: string, value: string) => void;
}

const TAB_LABELS = ["English", "中文", "Français"];

export const BannerContentTabs = ({ banner, onFieldChange }: Props) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);

  const fieldLabels: Record<BannerContentField, string> = {
    eyebrow: t(messages.banner_eyebrow()),
    headline: t(messages.banner_headline()),
    titleBadge: t(messages.banner_title_badge()),
    subtitle: t(messages.banner_subtitle()),
    cta: t(messages.banner_cta()),
    footnote: t(messages.banner_footnote()),
  };

  const fieldTips: Record<BannerContentField, string> = {
    eyebrow: t(messages.banner_tip_eyebrow()),
    headline: t(messages.banner_tip_headline()),
    titleBadge: t(messages.banner_tip_title_badge()),
    subtitle: t(messages.banner_tip_subtitle()),
    cta: t(messages.banner_tip_cta()),
    footnote: t(messages.banner_tip_footnote()),
  };

  const filledBySuffix = useMemo(
    () =>
      BANNER_LOCALE_SUFFIXES.map((suffix) =>
        BANNER_CONTENT_FIELDS.some((base) =>
          Boolean(
            (banner as Record<string, unknown> | undefined)?.[
              bannerContentField(base, suffix)
            ],
          ),
        ),
      ),
    [banner],
  );

  const activeSuffix: BannerLocaleSuffix = BANNER_LOCALE_SUFFIXES[selectedTab];

  const {
    canTranslate,
    emptyFields,
    pendingFields,
    isTranslatingAll,
    translateField,
    translateEmptyFields,
  } = useTranslateBannerContent({
    banner,
    suffix: activeSuffix,
    onFieldChange,
  });

  const englishValue = (base: BannerContentField): string =>
    String(
      (banner as Record<string, unknown> | undefined)?.[base] ?? "",
    ).trim();

  const translateAdornment = (base: BannerContentField) => {
    if (!canTranslate) return undefined;

    const isPending = pendingFields.includes(base);
    const hasSource = Boolean(englishValue(base));
    const label = hasSource
      ? t(messages.banner_translate_field())
      : t(messages.banner_translate_no_source());

    return (
      <InputAdornment position="end">
        <Tooltip title={label} placement="top" arrow>
          <span>
            <IconButton
              size="small"
              edge="end"
              aria-label={label}
              disabled={!hasSource || isPending || isTranslatingAll}
              onClick={() => translateField(base)}
              data-testid={`banner-translate-${bannerContentField(
                base,
                activeSuffix,
              )}`}
            >
              {isPending ? (
                <CircularProgress size={14} />
              ) : (
                <TranslateIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </InputAdornment>
    );
  };

  return (
    <>
      <Tabs
        value={selectedTab}
        onChange={(_event, next) => setSelectedTab(next)}
        sx={{
          minHeight: 36,
          mb: 2,
          borderBottom: `1px solid ${token.color.border}`,
          "& .MuiTabs-indicator": { backgroundColor: token.color.accent },
          "& .MuiTab-root": {
            minHeight: 36,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 600,
            color: token.color.textMuted,
            "&.Mui-selected": { color: token.color.accent },
          },
        }}
      >
        {TAB_LABELS.map((label, index) => (
          <Tab
            key={label}
            label={
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                {label}
                <span
                  aria-hidden="true"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: filledBySuffix[index]
                      ? token.color.accent
                      : "transparent",
                  }}
                />
              </span>
            }
          />
        ))}
      </Tabs>

      <Callout>{t(messages.banner_fallback_note())}</Callout>

      {canTranslate && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
          <Tooltip
            title={t(messages.banner_translate_all_tooltip())}
            placement="top"
            arrow
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  isTranslatingAll ? (
                    <CircularProgress size={14} />
                  ) : (
                    <TranslateIcon sx={{ fontSize: 16 }} />
                  )
                }
                disabled={!emptyFields.length || isTranslatingAll}
                onClick={translateEmptyFields}
                data-testid="banner-translate-all"
                sx={{
                  textTransform: "none",
                  fontSize: 13,
                  borderColor: token.color.border,
                  color: token.color.text,
                  "&:hover": {
                    borderColor: token.color.accent,
                    backgroundColor: token.color.accentSoft,
                  },
                }}
              >
                {t(messages.banner_translate_all())}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      )}

      <Grid container spacing={2.5}>
        {BANNER_CONTENT_FIELDS.map((base) => {
          const key = bannerContentField(base, activeSuffix);
          const limit = bannerFieldLimit(base, activeSuffix);
          const value =
            ((banner as Record<string, unknown> | undefined)?.[
              key
            ] as string) ?? "";
          return (
            <Grid item xs={12} md={6} key={key}>
              <Field
                id={`banner-content-${key}`}
                label={fieldLabels[base]}
                tooltip={fieldTips[base]}
                counter={{ value: value.length, max: limit }}
              >
                <TextControl
                  id={`banner-content-${key}`}
                  value={value}
                  error={value.length > limit}
                  onChange={(event) => onFieldChange(key, event.target.value)}
                  inputProps={{ "data-testid": `banner-content-${key}` }}
                  InputProps={{ endAdornment: translateAdornment(base) }}
                />
              </Field>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};

export default BannerContentTabs;
